// 移植自 crm/src/components/CommonButton.jsx。共用按鈕，依 Figma「CommonButton」（node 3008:1963）：
//   variant 'basic'    綠底白字膠囊；sm=Medium 12、big=Bold 14
//   variant 'icon'     深色底＋leading icon；sm=rounded-base、big=rounded-round
//   variant 'dropdown' （固定 big）icon+text 左段＋分隔線＋caret 右段
// hover：底色疊 10% #202221 變深（寫絕對值：brand-black-1000 在暗色主題會反轉成近白，hover 會變亮）
// props：variant、size（'sm'|'big'，預設 big）、icon、disabled、loading（送出中：維持原底色＋白色 Spin）、
//        onClick、onCaretClick（dropdown）、type、form、tabIndex、className、children
// disabled 與 loading：disabled＝未達送出條件（灰底）、loading＝已送出處理中（原底色＋轉圈），兩者可並用，loading 優先決定底色
import { CaretDownIcon } from './icons'
import { Spin } from './Spin'

// 完整字面常數：不可在 class 內插值，否則 Tailwind 掃不到
const HOVER_GREEN = 'enabled:hover:bg-[color-mix(in_srgb,var(--color-brand-primary)_90%,#202221)]'
const HOVER_DARK = 'enabled:hover:bg-[color-mix(in_srgb,var(--color-brand-black-900)_90%,#202221)]'
const ICON_SHADOW = 'shadow-[0px_3px_9px_0px_#2022210d,inset_0px_0px_4px_0px_rgba(255,255,255,0.25)]'
const GLOW_SHADOW = 'shadow-[inset_0px_0px_4px_0px_rgba(255,255,255,0.25)]'

const BASIC_BASE = `inline-flex items-center justify-center whitespace-nowrap rounded-full border border-brand-primary bg-brand-primary text-white transition-colors ${HOVER_GREEN} enabled:hover:border-transparent disabled:cursor-not-allowed disabled:border-transparent`
const ICON_BASE = `inline-flex items-center justify-center gap-0.5 whitespace-nowrap bg-brand-black-900 text-brand-black-0 transition-colors ${ICON_SHADOW} ${HOVER_DARK} disabled:cursor-not-allowed disabled:text-white disabled:shadow-[0px_3px_9px_0px_#2022210d]`
// disabled 底色分開成常數：不能同時掛兩個 disabled:bg-*，生效順序由 CSS 產出順序決定、不可控
const DISABLED_BG = 'disabled:bg-brand-black-200'
const LOADING_BG_GREEN = 'disabled:bg-brand-primary'
const LOADING_BG_DARK = 'disabled:bg-brand-black-900'
const SIZE = {
  basic: {
    sm: 'h-7 px-3 py-1.25 text-xs font-medium leading-4.5 tracking-[0.12px]',
    big: 'px-6 py-2 text-sm font-bold leading-5 tracking-[0.14px]',
  },
  icon: {
    sm: 'px-3 py-1.25 rounded-brand-base text-xs font-normal leading-4.5 tracking-[0.12px]',
    big: 'h-9 px-4 py-1.75 rounded-brand-round text-sm font-normal leading-5 tracking-[0.14px]',
  },
}

export function CommonButton({ variant = 'basic', size: sizeProp, icon, disabled, loading, onClick, onCaretClick, type = 'button', form, tabIndex, className = '', children }) {
  if (variant === 'dropdown') {
    return (
      <div className={`inline-flex drop-shadow-[0px_3px_4.5px_#2022210d] ${className}`}>
        <button
          type="button"
          disabled={disabled}
          onClick={onClick}
          className={`flex h-9 items-center gap-0.5 whitespace-nowrap rounded-l-brand-round bg-brand-black-900 px-4 py-1.75 text-sm font-normal leading-5 tracking-[0.14px] text-brand-black-0 transition-colors ${GLOW_SHADOW} ${HOVER_DARK} disabled:cursor-not-allowed disabled:bg-brand-black-200 disabled:text-white`}
        >
          {icon}
          {children}
        </button>
        <button
          type="button"
          disabled={disabled}
          onClick={onCaretClick}
          className={`flex h-9 items-center justify-center rounded-r-brand-round border-l border-brand-black-700 bg-brand-black-900 px-2.5 py-1.75 text-brand-black-0 transition-colors ${GLOW_SHADOW} ${HOVER_DARK} disabled:cursor-not-allowed disabled:border-brand-black-100 disabled:bg-brand-black-200 disabled:text-white`}
        >
          <CaretDownIcon className="w-2.5" />
        </button>
      </div>
    )
  }

  const size = sizeProp ?? 'big'
  const isIcon = variant === 'icon'
  return (
    <button
      type={type}
      form={form}
      tabIndex={tabIndex}
      disabled={disabled || loading}
      onClick={onClick}
      className={`${isIcon ? ICON_BASE : BASIC_BASE} ${loading ? (isIcon ? LOADING_BG_DARK : LOADING_BG_GREEN) : DISABLED_BG} ${SIZE[isIcon ? 'icon' : 'basic'][size]} ${className}`}
    >
      {isIcon && icon}
      {loading ? (
        <span className={size === 'sm' ? 'flex h-4.5 items-center justify-center' : 'flex h-5 items-center justify-center'}>
          <Spin size="sm" className="text-white" />
        </span>
      ) : (
        children
      )}
    </button>
  )
}
